import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthContext';
import { useStopImpersonation } from '@/lib/adminApi';

/**
 * ImpersonationBanner — sticky yellow top banner, visible on every
 * authenticated page whenever the API-backed session reports `impersonatedBy`.
 *
 * Mounted once at the app root (main.tsx). Renders null when not
 * impersonating so it is zero-cost for the common case.
 *
 * Clicking [Stop impersonation] calls the stop mutation on the API, signs
 * out locally, and navigates to /login so the admin can re-authenticate as
 * themselves.
 */
export default function ImpersonationBanner() {
  const { user, impersonatedBy, signOut } = useAuth();
  const stopMut = useStopImpersonation();
  const navigate = useNavigate();

  if (!impersonatedBy) return null;

  async function handleStop() {
    try {
      await stopMut.mutateAsync();
    } catch {
      toast.error("Couldn't stop impersonation — try again");
      return;
    }
    try {
      await signOut();
    } catch {
      // Even if signOut fails locally, continue — the backend has
      // revoked the impersonation and the user should re-authenticate.
    }
    navigate('/login');
  }

  return (
    <div
      role="status"
      className="sticky top-0 z-40 bg-[#FEF3C7] text-[#92400E] border-b border-[#F59E0B] px-4 py-2 text-sm flex items-center justify-between"
    >
      <span>
        You are impersonating <strong>{user?.email ?? '(unknown user)'}</strong>. All actions are
        logged.
      </span>
      <button
        type="button"
        onClick={handleStop}
        disabled={stopMut.isPending}
        className="ml-4 inline-flex items-center rounded-md border border-[#92400E] px-3 py-1 text-xs font-medium hover:bg-[#FDE68A] disabled:opacity-50"
      >
        Stop impersonation
      </button>
    </div>
  );
}
